// Supabase client for centralized data storage
import { createClient } from '@supabase/supabase-js';
import { authFetch } from './authFetch';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '');

// ============================================
// Data Storage Functions (replaces IndexedDB)
// ============================================

export async function loadUserData(clerkUserId) {
  if (!clerkUserId) {
    console.error('No clerk user ID provided');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('user_data')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (error) {
      // No data found - return null (new user)
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    // Convert from DB format to app format (tasks removed)
    return {
      counterparts: data.counterparts || [],
      activities: data.activities || [],
      favorites: data.favorites || [],
      recentItems: data.recent_items || [],
      technologies: data.technologies || [],
      stages: data.stages || [],
      pipelineStatuses: data.pipeline_statuses || [],
      counterpartCategories: data.counterpart_categories || [],
      regions: data.regions || [],
      counterpartTags: data.counterpart_tags || [],
      theme: data.theme || 'dark',
      sectorConfig: data.sector_config || {},
    };
  } catch (error) {
    console.error('Error loading user data from Supabase:', error);
    throw error;
  }
}

export async function saveUserData(clerkUserId, userData) {
  if (!clerkUserId) {
    console.error('No clerk user ID provided');
    return false;
  }

  try {
    // Convert from app format to DB format (tasks, projects, documents removed)
    const dbData = {
      clerk_user_id: clerkUserId,
      counterparts: userData.counterparts || [],
      activities: userData.activities || [],
      favorites: userData.favorites || [],
      recent_items: userData.recentItems || [],
      technologies: userData.technologies || [],
      stages: userData.stages || [],
      pipeline_statuses: userData.pipelineStatuses || [],
      counterpart_categories: userData.counterpartCategories || [],
      regions: userData.regions || [],
      counterpart_tags: userData.counterpartTags || [],
      theme: userData.theme || 'dark',
      sector_config: userData.sectorConfig || {},
      last_sync_at: new Date().toISOString(),
    };

    // Upsert - insert or update
    const { error } = await supabase
      .from('user_data')
      .upsert(dbData, { onConflict: 'clerk_user_id' });

    if (error) throw error;

    return true;
  } catch (error) {
    console.error('Error saving user data to Supabase:', error);
    throw error;
  }
}

// Check if we can connect to Supabase
export async function checkSupabaseConnection() {
  try {
    const { error } = await supabase.from('user_data').select('id').limit(1);
    return !error;
  } catch {
    return false;
  }
}

// ============================================
// CENTRALIZED DATA FUNCTIONS
// ============================================

// Load all published deals
export async function loadDeals() {
  try {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .eq('content_status', 'published')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error loading deals:', error);
    return [];
  }
}

// Load all published counterparts - via API to bypass RLS
export async function loadCounterparts() {
  try {
    const cb = sessionStorage.getItem('counterparts_cache_bust');
    const url = cb ? `/api/counterparts?_cb=${cb}` : '/api/counterparts';
    if (cb) sessionStorage.removeItem('counterparts_cache_bust');
    const response = await authFetch(url);
    if (!response.ok) {
      console.error('[loadCounterparts] API error:', response.status);
      throw new Error('Failed to load counterparts');
    }
    const result = await response.json();
    const list = result.counterparts || [];
    // Attach totalCount non-enumerably so it survives array spreads/maps.
    if (typeof result.totalCount === 'number') {
      Object.defineProperty(list, 'totalCount', { value: result.totalCount, enumerable: false });
    }
    return list;
  } catch (error) {
    console.error('Error loading counterparts:', error);
    return [];
  }
}

// Load counterpart dimensions separately (deferred from main list for ~3.2 MB savings)
export async function loadCounterpartDimensions() {
  try {
    const response = await authFetch('/api/counterparts?fields=dimensions');
    if (!response.ok) {
      console.error('[loadCounterpartDimensions] API error:', response.status);
      throw new Error('Failed to load dimensions');
    }
    const result = await response.json();
    return result.dimensions || [];
  } catch (error) {
    console.error('Error loading counterpart dimensions:', error);
    return [];
  }
}

// Load deal-counterpart links
export async function loadDealCounterparts() {
  try {
    const { data, error } = await supabase
      .from('deal_counterparts')
      .select(`
        *,
        counterpart:counterparts(id, name, type, categories)
      `);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error loading deal counterparts:', error);
    return [];
  }
}

// Helper to normalize deal fields that might be objects
function normalizeDeal(deal) {
  if (!deal) return deal;

  // Normalize capacity - convert object to number
  if (deal.capacity && typeof deal.capacity === 'object') {
    const capUnit = deal.capacity.unit || deal.capacity_unit || 'MW';
    deal.capacity_unit = capUnit;
    deal.capacity = deal.capacity.amount || deal.capacity.value || null;
  }

  // Normalize annual_generation
  if (deal.annual_generation && typeof deal.annual_generation === 'object') {
    deal.annual_generation = deal.annual_generation.amount || deal.annual_generation.value || null;
  }

  // Normalize value field if it exists
  if (deal.value && typeof deal.value === 'object') {
    const currency = deal.value.currency || '$';
    const unit = deal.value.unit || 'M';
    const amount = deal.value.amount || deal.value.value;
    deal.value = amount ? `${currency}${amount}${unit}` : null;
  }

  return deal;
}

// Load deals with their counterparts - via API to bypass RLS
export async function loadDealsWithCounterparts() {
  try {
    const cb = sessionStorage.getItem('deals_cache_bust');
    const url = cb ? `/api/deals?_cb=${cb}` : '/api/deals';
    if (cb) sessionStorage.removeItem('deals_cache_bust');
    const response = await authFetch(url);
    if (!response.ok) {
      console.error('[loadDealsWithCounterparts] API error:', response.status);
      throw new Error('Failed to load deals');
    }
    const result = await response.json();
    // Normalize all deals to handle any object fields
    return (result.deals || []).map(normalizeDeal);
  } catch (error) {
    console.error('Error loading deals with counterparts:', error);
    return [];
  }
}

// Load all centralized data at once
export async function loadCentralizedData() {
  try {
    const [deals, counterparts] = await Promise.all([
      loadDealsWithCounterparts(),
      loadCounterparts()
    ]);

    return { deals, counterparts };
  } catch (error) {
    console.error('Error loading centralized data:', error);
    return { deals: [], counterparts: [] };
  }
}

// Load Bloomberg-style dashboard statistics
export async function loadDashboardStats() {
  try {
    const cb = sessionStorage.getItem('dashboard_cache_bust');
    const url = cb ? `/api/dashboard-stats?_cb=${cb}` : '/api/dashboard-stats';
    if (cb) sessionStorage.removeItem('dashboard_cache_bust');
    // authFetch, not fetch: since 2026-09-26 the endpoint returns 20 recent deals
    // to anonymous callers and the full 200 to signed-in users.
    const response = await authFetch(url);
    if (!response.ok) {
      console.error('[loadDashboardStats] API error:', response.status);
      throw new Error('Failed to load dashboard stats');
    }
    const result = await response.json();
    return result.stats || null;
  } catch (error) {
    console.error('Error loading dashboard stats:', error);
    return null;
  }
}
